<template>
  <v-container fluid>
    <section>
      <!-- Summary Cards (4 Cards) -->
      <KpiCards class="mb-3" :items="kpiItems" :loading="loadingSummary" :skeleton="4" />

      <data-table
        :headers="headers"
        :items="productOptions"
        commit="SET_PRODUCT_OPTIONS"
        :viewItem="viewItem"
        :editItem="editItem"
        :deleteItem="deleteItem"
      >
        <div slot="activator" class="row justify-content-between mb-4">
          <div class="col-xs-12 col-sm-12 col-md-12 col-lg-2">
            <v-card-title class="px-0 pt-0 ps-products-title">
              {{ $t("product_options") }}
            </v-card-title>
          </div>
          <div class="col-xs-12 col-sm-12 col-md-12 col-lg-3 offset-lg-5">
            <TextSearch
              v-model="search"
              @search="fetchItem()"
              @onClear="
                search = '';
                fetchItem();
              "
            />
          </div>
          <div class="col-xs-12 col-sm-12 col-md-12 col-lg-2 text-end">
            <v-btn
              depressed
              color="primary"
              class="white--text btn-primary-brand"
              @click="newItem"
            >
              <v-icon left small>fal fa-plus</v-icon>
              {{ $t("add_new_option") }}
            </v-btn>
          </div>
        </div>
      </data-table>

      <!-- View Dialog -->
      <app-custom-dialog
        v-model="dialog"
        :footer="false"
        title="option_details"
      >
        <div slot="activator">
          <v-row>
            <v-col cols="4">{{ $t("id") }}</v-col>
            <v-col cols="8">{{ form.id }}</v-col>
            <v-col cols="4">{{ $t("option_name") }}</v-col>
            <v-col cols="8">{{ form.name }}</v-col>
            <v-col cols="4">{{ $t("option_values") }}</v-col>
            <v-col cols="8">
              <v-chip
                v-for="(value, index) in form.values"
                :key="index"
                class="ma-1"
                color="primary"
                outlined
              >
                {{ value }}
              </v-chip>
            </v-col>
            <v-col cols="4">{{ $t("product_options_count") }}</v-col>
            <v-col cols="8">{{ form.product_options_count || 0 }}</v-col>
            <v-col cols="4">{{ $t("status") }}</v-col>
            <v-col cols="8">
              <v-chip :color="form.is_active ? 'green' : 'red'" outlined>
                {{ form.is_active ? $t("active") : $t("inactive") }}
              </v-chip>
            </v-col>
            <v-col cols="4">{{ $t("date") }}</v-col>
            <v-col cols="8">{{ form.created_at | moment }}</v-col>
          </v-row>
        </div>
      </app-custom-dialog>

      <!-- Add/Edit Dialog -->
      <app-custom-dialog
        v-model="dialogAdd"
        :footer="true"
        :title="titleStatus"
        @submit="submitForm"
      >
        <div slot="activator">
          <TTextField
            label="option_name"
            :errorMessages="nameError"
            v-model="form.name"
            @input="$v.form.name.$touch()"
            @blur="$v.form.name.$touch()"
          />

          <div class="mt-4">
            <div class="text-caption grey--text mb-2">
              {{ $t("option_values") }}
            </div>

            <!-- Combobox for adding values -->
            <div class="d-flex align-center mb-2">
              <v-combobox
                v-model="newValue"
                :items="availableValues"
                :label="$t('add_value')"
                class="flex-grow-1 mr-2"
                @keyup.enter="addValue"
                @change="addValue"
                clearable
                :search-input.sync="searchInput"
              />
              <v-btn
                small
                color="primary"
                @click="addValue"
                :disabled="!newValue || form.values.includes(newValue)"
              >
                <v-icon small>fal fa-plus</v-icon>
              </v-btn>
            </div>

            <!-- Display current values -->
            <div v-if="form.values.length > 0" class="mt-2">
              <v-chip
                v-for="(value, index) in form.values"
                :key="index"
                class="ma-1"
                color="primary"
                outlined
                close
                @click:close="removeValue(index)"
              >
                {{ value }}
              </v-chip>
            </div>

            <div
              v-if="form.values.length === 0"
              class="text-caption grey--text"
            >
              {{ $t("no_values_added") }}
            </div>

            <!-- Status toggle -->
            <div class="mt-4">
              <v-switch
                v-model="form.is_active"
                :label="$t('active')"
                color="primary"
              />
            </div>
          </div>
        </div>
      </app-custom-dialog>

      <app-loading :overlay="overlay" />
    </section>
  </v-container>
</template>

<script>
import DataTable from "@/components/DataTable";
import { mapGetters, mapActions } from "vuex";
import { myMixin } from "@/mixin/main";
import { success, confirm, errorAlert } from "@/controllers/alert";
import TextSearch from "@/components/global/TextSearch";
import TTextField from "@/components/global/TextField";
import { required, minLength } from "vuelidate/lib/validators";
import { productOptionHead } from "@/utils/head";

export default {
  mixins: [myMixin],
  data: () => ({
    overlay: false,
    search: "",
    update: false,
    dialogAdd: false,
    dialog: false,
    loadingSummary: true,
    summaries: [],
    form: {
      id: null,
      name: "",
      values: [],
      is_active: true,
      product_options_count: 0,
    },
    newValue: "",
    searchInput: "",
  }),

  async created() {
    await Promise.all([this.fetchItem(), this.fetchSummary()]);
  },

  components: {
    DataTable,
    TextSearch,
    TTextField,
  },

  computed: {
    kpiItems() {
      return (this.summaries || []).map((item, index) => ({
        key: index,
        title: item.title,
        icon: item.icon,
        value: item.total ?? 0,
        note: item.description ? this.$t(item.description) : "",
      }));
    },
    ...mapGetters({
      productOptions: "index/productOptions",
    }),

    headers() {
      return productOptionHead(this);
    },

    titleStatus() {
      return this.update ? "edit_options" : "new_option";
    },

    nameError() {
      const errors = [];
      if (this.$v.form.name.$dirty) {
        if (!this.$v.form.name.required) {
          errors.push(this.$t("option_name_required"));
        }
        if (!this.$v.form.name.minLength) {
          errors.push(this.$t("option_name_min_length"));
        }
      }
      return errors;
    },

    // Get all available values for combobox suggestions
    availableValues() {
      const allValues = [];
      if (this.productOptions && this.productOptions.data) {
        this.productOptions.data.forEach((option) => {
          // Use predefined_values from backend (preferred) or fallback to product_options
          const values =
            option.predefined_values || option.product_options || [];
          if (Array.isArray(values)) {
            allValues.push(...values);
          }
        });
      }
      // Remove duplicates and sort (in case there are duplicates across options)
      return [...new Set(allValues)].sort();
    },
  },

  validations: {
    form: {
      name: {
        required,
        minLength: minLength(2),
      },
    },
  },

  methods: {
    ...mapActions({
      fetch: "index/fetch",
    }),

    async fetchItem() {
      try {
        this.overlay = true;
        const body = {
          url: `store_options?q=${this.search}`,
          that: this,
          commit: "SET_PRODUCT_OPTIONS",
        };
        await this.fetch(body);
      } catch (error) {
        console.log(error);
        await errorAlert(this, "Error fetching options");
      } finally {
        this.overlay = false;
      }
    },

    async fetchSummary() {
      this.loadingSummary = true;
      try {
        const { data } = await this.$http.get("get_summary_product_options");
        if (data && data.status) {
          this.summaries = data.data;
        }
      } catch (error) {
        console.log("error fetching product option summary", error);
      } finally {
        this.loadingSummary = false;
      }
    },

    newItem() {
      this.update = false;
      this.form = {
        id: null,
        name: "",
        values: [],
        is_active: true,
        product_options_count: 0,
      };
      this.newValue = "";
      this.searchInput = "";
      this.dialogAdd = true;
    },

    editItem(item) {
      this.update = true;
      this.form = {
        id: item.id,
        name: item.name,
        values: item.predefined_values || item.product_options || [],
        is_active: item.is_active,
        product_options_count: item.product_options_count,
      };
      this.newValue = "";
      this.searchInput = "";
      this.dialogAdd = true;
    },

    viewItem(item) {
      this.form = {
        ...item,
        values: item.predefined_values || item.product_options || [],
      };
      this.dialog = true;
    },

    async deleteItem(item) {
      const { isConfirmed } = await confirm({
        props: this,
        text: "want_to_delete_option",
      });

      if (isConfirmed) {
        try {
          this.overlay = true;
          const { data } = await this.$http.delete(`store_options/${item.id}`);
          if (data.status) {
            await this.fetchItem();
            await this.fetchSummary();
            await success(this, data.message);
          } else {
            await errorAlert(this, data.message);
          }
        } catch (error) {
          console.log(error);
          await errorAlert(this, "Error deleting option");
        } finally {
          this.overlay = false;
        }
      }
    },

    addValue() {
      if (this.newValue && this.newValue.trim()) {
        const value = this.newValue.trim();
        if (!this.form.values.includes(value)) {
          this.form.values.push(value);
          this.form.values.sort(); // Keep values sorted
        }
        this.newValue = "";
        this.searchInput = "";
      }
    },

    removeValue(index) {
      this.form.values.splice(index, 1);
    },

    async submitForm() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      if (this.form.values.length === 0) {
        await errorAlert(this, "Please add at least one option value");
        return;
      }

      try {
        this.overlay = true;
        const url = this.update
          ? `store_options/${this.form.id}`
          : "store_options";
        const method = this.update ? "put" : "post";

        const payload = {
          name: this.form.name,
          values: this.form.values,
          is_active: this.form.is_active,
        };

        const { data } = await this.$http[method](url, payload);

        if (data.status) {
          await this.fetchItem();
          await this.fetchSummary();
          this.dialogAdd = false;
          await success(this, data.message);
        } else {
          await errorAlert(this, data.message);
        }
      } catch (error) {
        console.log(error);
        await errorAlert(this, "An error occurred while saving");
      } finally {
        this.overlay = false;
      }
    },
  },
};
</script>

<style scoped>
.v-combobox {
  margin-bottom: 8px;
}

.ps-products-title {
  font-size: 1.25rem;
  font-weight: 900;
  color: #141021;
}
</style>
